// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ButtonIcon } from "@insite/mobius/Button";
import buttonShapes from "@insite/mobius/Button/buttonShapes";
import buttonSizeVariants from "@insite/mobius/Button/buttonSizeVariants";
import ButtonPresentationPropsDefault from "@insite/mobius/Button/presentationProps";
import { ComponentThemeProps } from "@insite/mobius/globals/baseTheme";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import get from "@insite/mobius/utilities/get";
import getContrastColor from "@insite/mobius/utilities/getContrastColor";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import { xBaseTheme } from "@mcmc/XDL/types";
import { css } from "styled-components";

(buttonShapes as any).rectangle = ({ _color, theme, buttonType, _sizeVariant, _size }: any) => {
    if (buttonType !== "solid") {
        return css``;
    }

    let hoverColor;
    let activeColor;

    switch (_color) {
        case "primary":
            hoverColor = "secondary";
            activeColor = "primary";
            break;
        case "secondary":
            hoverColor = "primary";
            activeColor = "secondary";
            break;
        case "tertiary":
            hoverColor = "primary";
            activeColor = "tertiary";
            break;
        default:
            hoverColor = _color;
            activeColor = _color;
    }
    const buttonHeight = _size || get(buttonSizeVariants, [_sizeVariant, "height"]);
    return css`
        display: inline-flex;
        min-height: 0;
        height: ${buttonHeight}px;
        backface-visibility: visible;

        &:focus {
            box-shadow: 1px 1px 1px transparent;
        }

        && {
            line-height: 1;
            white-space: nowrap;
            text-transform: uppercase;
            text-align: center;
            align-items: center;
            justify-content: center;
            ${_color === "tertiary"
                ? css`
                      border-color: ${resolveColor("text.inverse", theme)};
                  `
                : ""}
            ${ButtonIcon} {
                position: static;
            }
        }

        ${hoverColor
            ? `&&:not(:disabled):hover {
            background: ${resolveColor(hoverColor, theme)};
            border-color: ${resolveColor(hoverColor, theme)};
            color: ${getContrastColor(hoverColor, theme)};
        }`
            : ``}
        ${activeColor
            ? `&&:active {
            background: ${resolveColor(activeColor, theme)};
            border-color: ${resolveColor(activeColor, theme)};
            color: ${getContrastColor(activeColor, theme)};
        }`
            : ``}

        &&:disabled {
            background: ${resolveColor(_color, theme)};
            border-color: ${resolveColor(_color, theme)};
            color: ${getContrastColor(_color, theme)};
            opacity: 0.5;
        }
    `;
};

const commonButtonProps: ComponentThemeProps["button"]["defaultProps"] = {
    buttonType: "solid",
    sizeVariant: "large",
    mergeCss: true,
    typographyProps: {
        variant: "h6",
        forwardAs: "span",
        css: css`
            letter-spacing: 0;
            line-height: inherit;
            max-width: 100%;
            overflow: hidden;
            display: inline-block;
            text-overflow: ellipsis;
            margin-top: 0;
            margin-bottom: 0;
        `,
    },
};

buttonSizeVariants.large = {
    height: 40,
    padding: "0 25px",
    fontSize: 16,
    icon: 24,
    iconPadding: 5,
};

buttonSizeVariants.medium = {
    height: 35,
    padding: "0 20px",
    fontSize: 16,
    icon: 24,
    iconPadding: 5,
};

buttonSizeVariants.small = {
    height: 30,
    padding: "0 15px",
    fontSize: 14,
    icon: 24,
    iconPadding: 5,
};

export default {
    primary: {
        ...ButtonPresentationPropsDefault,
        color: "primary",
        ...commonButtonProps,
    },
    secondary: {
        ...ButtonPresentationPropsDefault,
        color: "secondary",
        ...commonButtonProps,
    },
    tertiary: {
        ...ButtonPresentationPropsDefault,
        color: "tertiary",
        ...commonButtonProps,
    },
} as xBaseTheme["button"];
